/**
 * SafeMedia — Patch 37.4.1.
 *
 * Renders an ``<img>`` or ``<video>`` with a deterministic fallback:
 *
 *   * When the ``src`` is missing/empty → renders a labelled empty
 *     state ("Preview not available yet") rather than nothing.
 *   * When the media fails to load (404, network, decode error) →
 *     swaps to the same labelled fallback so the founder never sees
 *     a broken-image icon or an empty video window.
 *
 * This component is intentionally **presentational** — no analytics,
 * no telemetry. It's a trust surface.
 */
import { useEffect, useState } from "react";
import { ImageOff, VideoOff } from "lucide-react";

interface Common {
  className?: string;
  testId?: string;
  emptyLabel?: string;
}

type SafeImageProps = Common & {
  kind: "image";
  src: string | null | undefined;
  alt?: string;
};

type SafeVideoProps = Common & {
  kind: "video";
  src: string | null | undefined;
  poster?: string | null;
  controls?: boolean;
  preload?: "none" | "metadata" | "auto";
};

export function SafeMedia(props: SafeImageProps | SafeVideoProps): JSX.Element {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [props.src]);

  const isMissing = !props.src || props.src.trim().length === 0;
  const shouldFallback = isMissing || failed;

  if (shouldFallback) {
    const Icon = props.kind === "video" ? VideoOff : ImageOff;
    const label =
      props.emptyLabel
      ?? (props.kind === "video"
        ? "Preview not available yet."
        : "No preview yet.");
    return (
      <div
        data-testid={props.testId ? `${props.testId}-fallback` : "safe-media-fallback"}
        data-media-fallback="true"
        className={
          "flex h-32 w-full flex-col items-center justify-center gap-1 rounded border border-dashed border-white/15 bg-white/[0.02] text-xs text-white/50 "
          + (props.className ?? "")
        }
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
        <span>{label}</span>
      </div>
    );
  }

  if (props.kind === "video") {
    return (
      <video
        data-testid={props.testId}
        src={props.src as string}
        poster={props.poster ?? undefined}
        controls={props.controls ?? true}
        preload={props.preload ?? "metadata"}
        className={props.className}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <img
      data-testid={props.testId}
      src={props.src as string}
      alt={props.alt ?? "preview"}
      className={props.className}
      onError={() => setFailed(true)}
    />
  );
}
